import { Box, CircularProgress, Container, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import { useSearchParams } from "react-router-dom";

import { ErrorView } from "components/common/ErrorView";
import { ErrorType } from "components/common/ErrorView/types";
import { NotesModal } from "components/detector/NotesModal";
import { BoundingBoxesProvider } from "context/boundingBoxesContext";
import { ImageSettingsProvider } from "context/ImageSettingsContext/imageSettingsContext";
import {
  CheckDisagreementResponse,
  DEFAULT_DISAGREEMENT_CHECK_RESPONSE,
  ReviewPageProvider,
} from "context/reviewPageContext/reviewPageContext";
import { DisagreementsContent } from "features/disagreementsContent/DisagreementsContent";
import { IDPMigrationModal } from "features/idpMigration/IDPMigrationModal";
import { PinnedNotesModal } from "features/pinnedNotesModal/PinnedNotesModal";
import { ReviewPageContent } from "features/reviewPageContent/ReviewPageContent";
import { ReviewPageSubNavBarNew } from "features/reviewPageSubNavBar/ReviewPageSubNavBarNew";
import useReviewPage from "hooks/Review/useReviewPage";
import { useIsAdminBrowsingBlocked } from "hooks/useIsAdminBrowsingBlocked/useIsAdminBrowsingBlocked";
import { useShouldSeeReviewOnboarding } from "hooks/useShouldSeeReviewOnboarding/useShouldSeeReviewOnboarding";
import { FullNotesButton } from "new-components/buttons/FullNotesButton";
import { Typography } from "new-components/typography/Typography";
import { Disagreement } from "types/disagreements";
import { DATA_TYPE } from "types/imageQueries";
import { User, UserProps } from "types/users";
import { stringClassNameMap } from "utils/disagreementsUtils";

import { ReviewOnboardingModal } from "./components/ReviewOnboardingModal";
import { useReviewPageSentryContext } from "./components/ReviewPageErrorBoundary";

export enum HeaderModeChoices {
  Detector = "Detector",
  Global = "all_review",
  Posicheck = "Posicheck",
  Unclear = "unsure_review",
  ViewQueryDetails = "view_query_details",
  Group = "group_review",
}

export const HeaderTitles = {
  [HeaderModeChoices.Global]: "Review Global Feed",
  [HeaderModeChoices.Unclear]: "Review Unclear Only",
  [HeaderModeChoices.ViewQueryDetails]: "View query details",
  [HeaderModeChoices.Group]: "Review Group Feed",
};

export function ReviewPageRoute(props: UserProps) {
  const { user } = props;
  const shouldSeeFirstUseOfReviewModal = useShouldSeeReviewOnboarding();
  const isAdminBrowsingBlocked = useIsAdminBrowsingBlocked();
  const [searchParams] = useSearchParams();

  if (isAdminBrowsingBlocked && !searchParams.get("detector_id") && !searchParams.get("group_id")) {
    return <ErrorView error={ErrorType.ADMIN_GLOBAL_REVIEW_BLOCKED} />;
  }

  // "dummy" scope prevents a bug in react-hotkeys-hook where disabling the last scope enables all.
  return (
    <Box id="image-query-review-route" className="h-[calc(100vh-100px)]" sx={{ width: "100%" }}>
      <HotkeysProvider initiallyActiveScopes={["dummy", "review"]}>
        <ImageSettingsProvider isOnReview>
          <BoundingBoxesProvider>
            <ReviewPage user={user} />
          </BoundingBoxesProvider>
        </ImageSettingsProvider>
      </HotkeysProvider>

      {shouldSeeFirstUseOfReviewModal && <ReviewOnboardingModal />}
    </Box>
  );
}

export function ReviewPage(props: { user: User }) {
  const { user } = props;

  const [disagreementCheckResponse, setDisagreementCheckResponse] = useState<CheckDisagreementResponse>(
    DEFAULT_DISAGREEMENT_CHECK_RESPONSE
  );
  const { isDisagreement } = disagreementCheckResponse;

  const {
    canUndo,
    currentDetector,
    poolFilterMode,
    imageQueryId,
    currentImageQuery,
    poolAttemptCounter,
    reviewPageSummary,
    showIDPMigration,
    isLabeling,
    openNotes,
    openPinnedNotes,
    isLabelerActive,
    setIsLabelerActive,
    handleOpenNotes,
    handleCloseNotes,
    handleClosePinnedNotes,
    undoLabel,
    removeRecentIQ,
    startPooling,
    labelImageQuery,
  } = useReviewPage({ user });

  useEffect(() => {
    if (currentImageQuery?.data_type === DATA_TYPE.DISAGREEMENT) {
      const disagreement = currentImageQuery as Disagreement;
      setDisagreementCheckResponse({
        disagreementId: currentImageQuery.id,
        isDisagreement: true,
        challengedAnswer: stringClassNameMap[disagreement.challenged_answer.class_name || ""],
        referenceAnswer: stringClassNameMap[disagreement.reference_answer.class_name || ""],
        cameFromIQ: false,
        cameFromDisagreement: true,
        isDisagreementUndo: false,
      });
    }
  }, [currentImageQuery, setDisagreementCheckResponse]);

  useReviewPageSentryContext({
    detectorId: currentDetector?.id,
    imageQueryId,
    labelingMode: currentImageQuery?.predictor?.mode,
    poolFilterMode,
    isLabelerOnly: user.only_labeler_ui,
    isDisagreement,
  });

  return (
    <Box id="image-query-review" className="h-full">
      <Stack sx={{ height: "100%", width: "100%" }}>
        <ReviewPageSubNavBarNew
          dataForHeaderSelection={reviewPageSummary}
          detector={currentDetector}
          imageQueryId={imageQueryId}
          mode={poolFilterMode}
          user={user}
          isLabeling={isLabeling}
          undoLabel={undoLabel}
          canUndo={canUndo && !isDisagreement}
          labelerActive={isLabelerActive}
          handleLabelStatusChange={(event) => setIsLabelerActive(event.target.checked)}
        />

        <Container
          id="image-query-review-container-temp"
          maxWidth={isDisagreement ? "md" : "xl"}
          sx={{
            height: "calc(100% - 50px)",
            py: "0px",
            px: { xs: "10px", sm: "20px", md: isDisagreement ? "0px" : "30px" },
          }}
        >
          {currentImageQuery !== null && (
            <ReviewPageProvider
              currentImageQuery={currentImageQuery}
              imageQueryId={imageQueryId}
              disagreementCheckResponse={disagreementCheckResponse}
              setDisagreementCheckResponse={setDisagreementCheckResponse}
              isLabeling={isLabeling}
              labelImageQuery={labelImageQuery}
              removeRecentIQ={removeRecentIQ}
              startPooling={startPooling}
            >
              <Box sx={{ height: "50px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography fontWeight="semibold" className="text-[#49494D]">
                    QUERY:
                  </Typography>
                  <Typography variant="subtitle" fontWeight="bold" className="text-primary-darker">
                    {currentImageQuery?.predictor.label_prompt}
                  </Typography>
                </Box>
                <FullNotesButton onClick={handleOpenNotes} />
              </Box>

              {isDisagreement ? <DisagreementsContent /> : <ReviewPageContent />}
            </ReviewPageProvider>
          )}

          {!currentImageQuery && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="100%"
              gap="20px"
            >
              {(() => {
                if (!user.only_labeler_ui) {
                  return poolAttemptCounter < 3 ? (
                    <CircularProgress size={70} />
                  ) : (
                    <Typography variant="subtitle" fontWeight="bold">
                      No image queries found
                    </Typography>
                  );
                }
                if (isLabelerActive) {
                  return (
                    <>
                      <CircularProgress size={70} />
                      <Typography variant="subtitle" fontWeight="bold">
                        Waiting for image queries...
                      </Typography>
                    </>
                  );
                }
                return (
                  <Typography variant="subtitle" fontWeight="bold">
                    Labeling Paused - Inactive State
                  </Typography>
                );
              })()}
            </Box>
          )}
        </Container>
      </Stack>
      {currentDetector && openNotes && (
        <NotesModal
          detector={currentDetector}
          ownerGroupId={currentDetector.owner?.id}
          ownerGroupName={currentDetector.owner?.name}
          openModal={openNotes}
          onClose={handleCloseNotes}
        />
      )}
      {currentImageQuery?.predictor.mode && openPinnedNotes && (
        <PinnedNotesModal
          detectorId={currentImageQuery.predictor.id}
          onOpenFullNotes={handleOpenNotes}
          onClosePinnedNotes={handleClosePinnedNotes}
        />
      )}
      {showIDPMigration && <IDPMigrationModal isOpen={showIDPMigration} user={user} />}
    </Box>
  );
}
